/**
 * @typedef {'regional' | 'tematico' | 'evento'} CommunityType
 */

/**
 * @typedef {Object} Community
 * @property {number} id
 * @property {string} name
 * @property {string} description
 * @property {string | null | undefined} image
 * @property {CommunityType} type
 * @property {number} order
 * @property {string | null | undefined} race_calendar_id
 * @property {string} city
 * @property {string} state
 * @property {string} url
 * @property {string} created_at
 * @property {string} updated_at
 */

/**
 * @typedef {Object} CommunityPayload
 * @property {string} name
 * @property {string} description
 * @property {string | null | undefined} image
 * @property {CommunityType} type
 * @property {number | undefined} order
 * @property {string | null | undefined} race_calendar_id
 * @property {string} city
 * @property {string} state
 * @property {string} url
 */

/**
 * @typedef {Object} PaginatedCommunities
 * @property {Community[]} data
 * @property {number} total
 * @property {number} page
 * @property {number} limit
 */

export const COMMUNITY_LIMIT = 20;

export const COMMUNITY_TYPES = [
  { value: 'regional', label: 'Regional' },
  { value: 'tematico', label: 'Temático' },
  { value: 'evento', label: 'Evento' },
];

const COMMUNITY_TYPE_VALUES = COMMUNITY_TYPES.map(({ value }) => value);
const OBJECT_ID_PATTERN = /^[a-f\d]{24}$/i;
const URL_PATTERN = /^https?:\/\/\S+$/i;
const PAYLOAD_FIELDS = [
  'name',
  'description',
  'image',
  'type',
  'order',
  'race_calendar_id',
  'city',
  'state',
  'url',
];

export function getCommunityTypeLabel(type) {
  const communityType = COMMUNITY_TYPES.find(({ value }) => value === type);
  return communityType ? communityType.label : type;
}

export function calculateCommunitySkip(page, limit = COMMUNITY_LIMIT) {
  const safePage = Number.isInteger(page) && page > 0 ? page : 1;
  return (safePage - 1) * limit;
}

export function buildCommunityListParams({
  name = '',
  type = '',
  page = 1,
  limit = COMMUNITY_LIMIT,
} = {}) {
  const params = {
    skip: calculateCommunitySkip(page, limit),
    limit,
  };
  const normalizedName = name.trim();

  if (normalizedName) params.name = normalizedName;
  if (COMMUNITY_TYPE_VALUES.includes(type)) params.type = type;

  return params;
}

export function readCommunityFilters(search = '') {
  const params = new URLSearchParams(search);
  const parsedPage = Number.parseInt(params.get('page'), 10);
  const type = params.get('type') || '';

  return {
    name: params.get('name') || '',
    type: COMMUNITY_TYPE_VALUES.includes(type) ? type : '',
    page: Number.isInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1,
  };
}

export function buildCommunityFilterSearch({ name = '', type = '', page = 1 }) {
  const params = new URLSearchParams();
  const normalizedName = name.trim();

  if (normalizedName) params.set('name', normalizedName);
  if (COMMUNITY_TYPE_VALUES.includes(type)) params.set('type', type);
  if (page > 1) params.set('page', String(page));

  const search = params.toString();
  return search ? `?${search}` : '';
}

export function isCommunityAdmin(profile) {
  if (!profile || !Array.isArray(profile.roles)) return false;

  return profile.roles.some(role => {
    const roleName =
      typeof role === 'string' ? role : role && (role.slug || role.name);
    return roleName === 'administrator';
  });
}

export function normalizeCommunityPayload(values = {}) {
  const raceCalendarId = String(values.race_calendar_id || '').trim();
  const image = String(values.image || '').trim();
  const order =
    values.order === '' || values.order === null || values.order === undefined
      ? 0
      : Number(values.order);

  return {
    name: String(values.name || '').trim(),
    description: String(values.description || '').trim(),
    image: image || null,
    type: values.type || '',
    order,
    race_calendar_id: raceCalendarId || null,
    city: String(values.city || '').trim(),
    state: String(values.state || '').trim(),
    url: String(values.url || '').trim(),
  };
}

export function validateCommunity(values) {
  const payload = normalizeCommunityPayload(values);
  const errors = {};

  if (!payload.name) errors.name = 'Nome é obrigatório.';
  if (!payload.description) errors.description = 'Descrição é obrigatória.';
  if (!COMMUNITY_TYPE_VALUES.includes(payload.type)) {
    errors.type = 'Selecione um tipo válido.';
  }
  if (!Number.isInteger(payload.order) || payload.order < 0) {
    errors.order = 'A ordem deve ser um número inteiro não negativo.';
  }
  if (!payload.city) errors.city = 'Cidade é obrigatória.';
  if (!payload.state) errors.state = 'Estado é obrigatório.';
  if (!payload.url) {
    errors.url = 'URL é obrigatória.';
  } else if (!URL_PATTERN.test(payload.url)) {
    errors.url = 'Informe uma URL iniciada por http:// ou https://.';
  }
  if (
    payload.race_calendar_id &&
    !OBJECT_ID_PATTERN.test(payload.race_calendar_id)
  ) {
    errors.race_calendar_id = 'Identificador da corrida inválido.';
  }

  return errors;
}

export function buildCommunityUpdatePayload(initialValues, nextValues) {
  const initial = normalizeCommunityPayload(initialValues);
  const next = normalizeCommunityPayload(nextValues);

  return PAYLOAD_FIELDS.reduce((payload, field) => {
    if (initial[field] !== next[field]) payload[field] = next[field];
    return payload;
  }, {});
}

const ERROR_MESSAGES = {
  community_not_found: 'Comunidade não encontrada.',
  race_calendar_not_found: 'Corrida vinculada não encontrada.',
  invalid_community_id: 'Identificador da comunidade inválido.',
  invalid_race_calendar_id: 'Identificador da corrida inválido.',
};

export function getCommunityErrorCode(error) {
  const responseData = error && error.response && error.response.data;
  if (!responseData) return '';

  const candidates = [
    responseData.code,
    responseData.error,
    responseData.message,
  ];

  return candidates.find(candidate => ERROR_MESSAGES[candidate]) || '';
}

export function getCommunityErrorMessage(
  error,
  fallback = 'Não foi possível concluir a operação.'
) {
  const status = error && error.response && error.response.status;
  const code = getCommunityErrorCode(error);

  if (code) return ERROR_MESSAGES[code];
  if (status === 401) return 'Sua sessão expirou. Entre novamente.';
  if (status === 403) return 'Você não possui permissão para esta ação.';

  return fallback;
}
